import { useCallback, useEffect, useRef } from 'react';

interface UseOcrDropzoneOpenRequestParams {
  canOpenDropzone: boolean;
  isDropzoneMounted?: boolean;
  onNeedMountDropzone?: () => void;
  onUploadReady?: (open: (() => void) | null) => void;
  openUploadRequestKey: number;
  seedLastRequestKey: number;
}

export const useOcrDropzoneOpenRequest = ({
  canOpenDropzone,
  isDropzoneMounted = true,
  onNeedMountDropzone,
  onUploadReady,
  openUploadRequestKey,
  seedLastRequestKey,
}: UseOcrDropzoneOpenRequestParams) => {
  const dropzoneOpenRef = useRef<(() => void) | null>(null);
  const lastOpenUploadRequestKeyRef = useRef(seedLastRequestKey);
  const pendingOpenUploadRef = useRef(false);

  const handleDropzoneReady = useCallback(
    (open: (() => void) | null) => {
      const safeOpen = open
        ? () => {
            if (!canOpenDropzone) return;
            open();
          }
        : null;

      dropzoneOpenRef.current = safeOpen;
      onUploadReady?.(safeOpen);

      if (safeOpen && pendingOpenUploadRef.current) {
        pendingOpenUploadRef.current = false;
        safeOpen();
      }
    },
    [canOpenDropzone, onUploadReady]
  );

  useEffect(() => {
    if (openUploadRequestKey === lastOpenUploadRequestKeyRef.current) {
      return;
    }

    lastOpenUploadRequestKeyRef.current = openUploadRequestKey;

    if (!canOpenDropzone) {
      return;
    }

    if (!isDropzoneMounted) {
      pendingOpenUploadRef.current = true;
      onNeedMountDropzone?.();
      return;
    }

    if (!dropzoneOpenRef.current) {
      pendingOpenUploadRef.current = true;
      return;
    }

    dropzoneOpenRef.current();
  }, [
    canOpenDropzone,
    isDropzoneMounted,
    onNeedMountDropzone,
    openUploadRequestKey,
  ]);

  return { handleDropzoneReady };
};
